import html2pdf from "html2pdf.js";
import ApexCharts from "apexcharts";

function getPdfOptions({ allowElementSplit = false } = {}) {
  return {
    margin: [8, 10, 8, 10],
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      // Keep 0 like Home Program — negative scrollY can blank the first page
      scrollY: 0,
      windowWidth: document.documentElement.scrollWidth,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    },
    // avoid-all pushes tall chart rows to page 2 and leaves page 1 nearly blank
    pagebreak: allowElementSplit
      ? { mode: ["css", "legacy"] }
      : { mode: ["avoid-all", "css", "legacy"] },
  };
}

/**
 * ApexCharts SVG often renders blank under html2canvas.
 * Swap each chart with a PNG from dataURI() for the PDF capture, then restore.
 */
async function replaceApexChartsWithImages(rootElement) {
  const markers = rootElement.querySelectorAll("[data-apex-chart-id]");
  const restoreFns = [];

  for (const marker of markers) {
    const chartId = marker.getAttribute("data-apex-chart-id");
    if (!chartId) {
      continue;
    }

    const chart = ApexCharts.getChartByID(chartId);
    if (!chart || typeof chart.dataURI !== "function") {
      continue;
    }

    const result = await chart.dataURI({ scale: 2 });
    const imgURI = result?.imgURI;
    if (!imgURI) {
      continue;
    }

    const liveChart = marker.querySelector("[data-apex-chart-live]");
    if (!liveChart) {
      continue;
    }

    const img = document.createElement("img");
    img.src = imgURI;
    img.alt = "Chart";
    img.setAttribute("data-apex-chart-snapshot", "true");
    img.style.width = "100%";
    img.style.height = "auto";
    // Keep chart cards short enough that a row can start on page 1 with the letterhead
    img.style.maxHeight = "240px";
    img.style.objectFit = "contain";
    img.style.display = "block";

    liveChart.style.display = "none";
    marker.appendChild(img);

    restoreFns.push(() => {
      img.remove();
      liveChart.style.display = "";
    });
  }

  await Promise.all(
    Array.from(rootElement.querySelectorAll("img[data-apex-chart-snapshot]")).map(
      (img) =>
        img.decode ? img.decode().catch(() => undefined) : Promise.resolve()
    )
  );

  return () => {
    restoreFns.forEach((restore) => restore());
  };
}

export async function downloadElementAsPdf(
  element,
  fileName,
  { replaceApexCharts = false } = {}
) {
  let restoreCharts = () => {};
  if (replaceApexCharts) {
    restoreCharts = await replaceApexChartsWithImages(element);
  }

  try {
    // Reset scroll so capture starts at the top of the report (not under the sticky bar)
    const previousScrollY = window.scrollY;
    element.scrollIntoView({ block: "start", behavior: "auto" });
    window.scrollTo(0, 0);
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));

    const pdfBlob = await html2pdf()
      .set(getPdfOptions({ allowElementSplit: replaceApexCharts }))
      .from(element)
      .outputPdf("blob");

    window.scrollTo(0, previousScrollY);

    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } finally {
    restoreCharts();
  }
}
